<template>
    <div class="inner-menu" id="inner-menu">
        <h1 
            class="menu__nav-title slide__text-el"
            aria-atomic="true"
            aria-live="polite"
            v-html="menuUserTitle"
        />
        <ul class="menu__nav-list slide__text-el" @keydown.esc.stop="resetActive(null)">
            <li
                v-for="(slide, x) in slides.slice(2, -1)" :key="x"
                :class="{
                    'is-stacked': isMobile() || isBrowserPortrait,
                    'is-active': isActive(x),
                    'has-submenu has-icon': 1
                }"
            >
                <button 
                    class="menu__nav-item title--dash text-link" 
                    :aria-expanded="isActive(x).toString()"
                    :aria-controls="`innerMenuToggle-${x}`"
                    :id="`innerMenuBtn-${x}`"
                    v-html="Array.isArray(slide.header) && capitalizedText(slide.header.join(' '))"
                    @click="handleActiveMenu(x)"
                />
                
                <component :is="transitionComponent" name="fade">
                <div 
                    v-show="submenuActive === x" 
                    class="inner-menu__toggle" 
                    role="region" 
                    :id="`innerMenuToggle-${x}`"
                    :aria-labelledby="`innerMenuBtn-${x}`"
                    :aria-describedby="`innerMenuDesc-${x}`"
                >
                    <p
                        :id="`innerMenuDesc-${x}`"
                        class="slide__text" 
                        v-html="slide.body" 
                    />

                    <Submenu :items="slide.circles">
                        <template v-slot:firstItem>
                            <li>
                                <button class="view-all" @click="gotoSection(x)">
                                    View All
                                </button>
                            </li>
                        </template>
                        <template v-slot:item="{ data }">
                            <router-link
                                role="link"
                                class="text-link"
                                :to="gotoStory(data.x, data.item)"
                                v-html="capitalizedText(data.item.text)"
                            />
                        </template>
                        <template v-slot:lastItem v-if="slide.title.includes('Best Self')">
                            <li>
                                <button class="text-link" @click="gotoSection(-1)">
                                    Purpose Driven
                                </button>
                            </li>
                        </template>
                    </Submenu>
                </div>
            </component>
                
            </li>
            <li>
                <RoadmapCta 
                    class="menu__nav-item title--dash text-link"
                    :className="null"
                />
            </li>
        </ul>
    </div>
</template>

<script>
import gsap from 'gsap'
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { mapGetters } from 'vuex'

import getDelta from '@/mixins/lib/getDelta'
import focusElement from '@/mixins/focusElement'
import { adjustTitleFromSlug, adjustSlugFromTitle, adjustStorySlugByPath } from '@/mixins/lib/textFormat'

import Submenu from '@/components/Ui/Submenu'
import AnimateHeight from '@/components/Ui/AnimateHeight'
import RoadmapCta from '@/components/Discover/RoadmapCta'

gsap.registerPlugin(ScrollToPlugin);

export default {
    props: {
        slides: {
            type: Array,
            required: true
        }
    },

    components: {
        Submenu,
        AnimateHeight,
        RoadmapCta,
    },

    inject: ['moduleType'],

    data() {
        return {
            activeEl: null,
            submenuActive: null,
            transitionComponent: '',
        }
    },

    computed: {
        menuUserTitle() {
            const { type: userType } = this.$store.state.user

            const userTypeText = userType === 'student'
                ? 'Student &amp; Recent Graduate'
                : 'Experienced Hire'
                
            return `Let's discover your purple <span class=dash>&mdash;</span><br> ${userTypeText}`
        },
        ...mapGetters({
            browserSize: 'browser/size',
            isBrowserMobile: 'browser/isMobile',
            isBrowserPortrait: 'browser/isTabletPortrait',
        }),
    },

    watch: {
        browserSize: {
            immediate: true,
            handler() {
                const transition = this.isBrowserMobile || this.isBrowserPortrait 
                    ? 'animate-height' 
                    : 'transition'

                this.transitionComponent = transition
            },
        },
    },

    methods: {
        resetActive( x ) {
            this.submenuActive = x
            this.activeEl && this.activeEl.focus()
        },
        isActive( x ) {
            return this.submenuActive === x
        },
        handleActiveMenu( x ) {
            this.submenuActive = this.submenuActive !== x ? x : null
            this.activeEl = document.activeElement

            // setTimeout(() => {
            //     this.submenuActive && document
            //         .querySelector('[aria-expanded="true"] + .inner-menu__toggle button').focus()
            // }, 700)
        },
        gotoSection( x ) {
            if ( this.isMobile() ) {
                const currentSectionSelector = `.slides .slide--${(x + 3)}`
                const currentSection = document.querySelector(currentSectionSelector)
                
                if ( !currentSection ) return;

                const onComplete = () => 
                    focusElement(`${currentSectionSelector} h1, ${currentSectionSelector} h2`, true)

                return gsap.to(window, getDelta(0, x + 3), {
                    scrollTo: {
                        y: currentSection, 
                        offsetY: 30,
                        onComplete,
                    },
                    ease: 'strong',
                })
            }

            this.$root.$emit('slide-goto', x + 2)
        },
        gotoStory(x, item) {
            const { text, media: author, story: { id }} = item
            
            if ( Array.isArray(id) ) {
                const { eContentLink } = this.$store.getters
                return {
                    name: eContentLink
                }
            }

            const { path } = this.$route
            const storySlug = adjustSlugFromTitle(text)
            const adjustedSlug = adjustStorySlugByPath(storySlug, path)
            return `${adjustedSlug}-${author.toLowerCase().replace('_', '-')}`
        },
        capitalizedText( slug ) {
            return adjustTitleFromSlug(slug, ' ')
        },
    },
}
</script>